import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup,
  CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { anpService } from "@/features/fiscal/services/fiscalService";

interface Props {
  value?:        number;   // anpId selecionado
  displayLabel?: string;   // texto do selecionado ("código — descrição")
  onChange:      (anpId: number, codigo: string, descricao: string) => void;
  placeholder?:  string;
  disabled?:     boolean;
}

export function AnpSearchCombobox({
  value,
  displayLabel,
  onChange,
  placeholder = "Buscar classificação ANP por código ou descrição...",
  disabled,
}: Props) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["anp-search", search],
    queryFn:  () => anpService.listar(search, 1, 50),
    enabled:  open && search.trim().length >= 2,
    staleTime: 15_000,
  });

  const results = data?.itens ?? [];
  const label = value && displayLabel ? displayLabel : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled}
          type="button"
        >
          <span className="truncate text-left">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[520px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Digite código ou descrição ANP..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!isLoading && (
              <CommandEmpty>
                {search.trim().length < 2
                  ? "Digite ao menos 2 caracteres para buscar..."
                  : "Nenhuma classificação ANP encontrada."}
              </CommandEmpty>
            )}
            <CommandGroup>
              {results.map(a => (
                <CommandItem
                  key={a.id}
                  value={String(a.id)}
                  onSelect={() => {
                    onChange(a.id, a.codigo ?? "", a.descricaoProduto ?? "");
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === a.id ? "opacity-100" : "opacity-0")} />
                  <div className="flex flex-col min-w-0">
                    <span className="font-mono text-sm">{a.codigo}</span>
                    <span className="text-xs text-muted-foreground truncate">{a.descricaoProduto}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
